import { LogOut } from "lucide-react";
import { useLocation } from "react-router";
import { PageHeader } from "@/components/gb/page-header";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/auth/AuthContext";
import { DebugSwitch } from "@/features/debug/debug-mode";
import { navItemForPath } from "./nav";
import { useProjectContext } from "./project-context";

/** The user chip and sign-out control. Shared by both shells. */
export function TopbarActions() {
  const { user, signOut } = useAuth();
  return (
    <div className="flex items-center gap-3">
      {/* Only for an account the platform's admin flagged (`architecture/0029`). */}
      <DebugSwitch />
      {user?.email && (
        <span className="hidden text-[0.82rem] font-semibold text-muted sm:inline">
          {user.email}
        </span>
      )}
      <Button variant="secondary" size="sm" onClick={signOut}>
        <LogOut size={15} />
        Sign out
      </Button>
    </div>
  );
}

/**
 * The white bar above the scroll column. The design puts only the eyebrow and
 * title here and leaves the right-hand side empty; we add the sign-out control,
 * which the mockup has nowhere to live but the real app needs.
 *
 * Presentational: it is told what to say. `ProjectTopbar` works the title out
 * for project routes, and the picker passes a fixed one.
 */
export function Topbar({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <header className="z-5 flex shrink-0 items-center gap-6 border-b border-border bg-surface px-[30px] py-4">
      <PageHeader eyebrow={eyebrow} title={title} />
      <div className="flex-1" />
      <TopbarActions />
    </header>
  );
}

/**
 * The topbar for routes under `/projects/:projectId`.
 *
 * The section is resolved by exact first-segment match against `NAV_ITEMS`, on
 * the path *below* the project prefix. The previous `pathname.startsWith(n.to)`
 * cannot survive that prefix — every path starts with `/projects/…`, so `/p`
 * items would match everything — and prefix matching was always slightly wrong
 * anyway. See `NavItem.matches`.
 */
export function ProjectTopbar() {
  const { pathname } = useLocation();
  const { project, projectId } = useProjectContext();

  const prefix = `/projects/${projectId}`;
  const rest = pathname.startsWith(prefix) ? pathname.slice(prefix.length) : "";
  const match = navItemForPath(rest);

  // An unmatched path under a project is an off-nav route; it titles itself
  // from the path rather than silently inheriting Today's header.
  const header = match
    ? { eyebrow: match.eyebrow(project.name), title: match.title }
    : { eyebrow: project.name, title: rest.replace(/^\//, "") || project.name };

  return <Topbar eyebrow={header.eyebrow} title={header.title} />;
}
